import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { routeTree } from "./routeTree.gen";
import { initSentry, Sentry } from "./lib/sentry";
import { initAnalytics, getAnalyticsContext } from "./lib/analytics";
import posthog from "posthog-js";
import { queryPersister, shouldDehydrateQuery } from "./lib/queryPersister";
import { checkAndRedirect } from "./lib/tenant-redirect";
import { AppErrorFallback } from "./components/ErrorFallback";
import { SentryUserSync } from "./components/SentryUserSync";
import "./i18n/config"; // i18n side-effect init — must be before render
import "./index.css";

// Init Sentry before anything else
initSentry();

// Create a query client with offline-first defaults
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 60, // 1 hour
      gcTime: 1000 * 60 * 60 * 24, // 24 hours
      networkMode: "offlineFirst",
      retry: 2,
    },
  },
});

// Create a router instance
const router = createRouter({
  routeTree,
  context: {},
  defaultPreload: "intent",
});

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

// Persist options for IndexedDB cache
const persistOptions = {
  persister: queryPersister,
  dehydrateOptions: {
    shouldDehydrateQuery,
  },
  maxAge: 1000 * 60 * 60 * 24, // 24 hours
};

async function init() {
  // Check for tenant redirect before rendering
  const shouldRedirect = await checkAndRedirect();
  if (shouldRedirect) {
    return; // Don't render app, redirect in progress
  }

  // Initialize PostHog analytics
  initAnalytics();

  // SPA $pageview — fires on every route change (including initial load)
  router.subscribe("onResolved", ({ toLocation, pathChanged }) => {
    if (pathChanged) {
      posthog.capture("$pageview", {
        $current_url: window.location.origin + toLocation.href,
        ...getAnalyticsContext(),
      });
    }
  });

  ReactDOM.createRoot(document.getElementById("root")!).render(
    <React.StrictMode>
      <Sentry.ErrorBoundary fallback={AppErrorFallback}>
        <PersistQueryClientProvider client={queryClient} persistOptions={persistOptions}>
          <SentryUserSync />
          <RouterProvider router={router} />
        </PersistQueryClientProvider>
      </Sentry.ErrorBoundary>
    </React.StrictMode>
  );
}

init();
